import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../utilities/axiosInstance";
import { filterRequest } from "../../utilities/apiManager";

let initialState = {
  isLoading: false,
  data: [],
  userDetails: null,
  currentUser: null, // logged-in user (sidebar / permissions)
  itemsCount: 0,
  pages: 0,
  error: null,
};

export const getCurrentUser = createAsyncThunk(
  "users/getCurrentUser",
  async (_, thunkApi) => {
    try {
      const { data } = await axiosInstance.get(`/users`);
      return thunkApi.fulfillWithValue(data);
    } catch (errors) {
      return thunkApi.rejectWithValue(errors.response.data);
    }
  }
);

export const getUsers = createAsyncThunk(
  "users/getUsers",
  async (args, thunkApi) => {
    try {
      const { page, size = 10, query } = args;
      const filter = filterRequest(args.filter);
      const { data } = await axiosInstance.get(
        `/users/all?page=${page}&size=${size}&search=${query}${filter}`
      );
      return thunkApi.fulfillWithValue(data);
    } catch (errors) {
      return thunkApi.rejectWithValue(errors.response.data);
    }
  }
);

export const createUser = createAsyncThunk(
  "users/createUser",
  async (args, thunkApi) => {
    try {
      const { data } = await axiosInstance.post(
        `/auth/register`,
        args
      );
      return thunkApi.fulfillWithValue(data);
    }
    catch (errors) {
      return thunkApi.rejectWithValue(errors.response.data);
    }
  }
);

export const updatePassword = createAsyncThunk(
  "users/updatePassword",
  async (args, thunkApi) => {
    try {
      const { data } = await axiosInstance.put(
        `/users/update_password/${args._id}`,
        args
      );
      return thunkApi.fulfillWithValue(data);
    }
    catch (errors) {
      return thunkApi.rejectWithValue(errors.response.data);
    }
  }
);

export const updateUser = createAsyncThunk(
  "users/updateUser",
  async (args, thunkApi) => {
    try {
      const { data } = await axiosInstance.put(
        `/users/${args._id}`,
        args.values
      );
      return thunkApi.fulfillWithValue(data);
    }
    catch (errors) {
      return thunkApi.rejectWithValue(errors.response.data);
    }
  }
);

export const deleteUser = createAsyncThunk(
  "users/deleteUser",
  async (args, thunkApi) => {
    try {
      const { data } = await axiosInstance.delete(
        `/users/${args._id}`
      );
      return thunkApi.fulfillWithValue(data);
    }
    catch (errors) {
      return thunkApi.rejectWithValue(errors.response.data);
    }
  }
);

export const getUserDetails = createAsyncThunk(
  "users/getUserDetails",
  async (args, thunkApi) => {
    try {
      const { data } = await axiosInstance.get(
        `/users/${args.user}`
      );
      return thunkApi.fulfillWithValue(data);
    }
    catch (errors) {
      return thunkApi.rejectWithValue(errors.response.data);
    }
  }
)

export const getOwnerDetails = createAsyncThunk(
  "users/getOwnerDetails",
  async (args, thunkApi) => {
    try {
      const { data } = await axiosInstance.get(
        `/owners/${args.owner}`
      );
      return thunkApi.fulfillWithValue(data);
    }
    catch (errors) {
      return thunkApi.rejectWithValue(errors.response.data);
    }
  }
)

export const blockProfile = createAsyncThunk(
  "users/blockProfile",
  async (args, thunkApi) => {
    try {
      const { data } = await axiosInstance.post(
        `/users/profile/block`,
        { userId: args.userId, blockReason: args.blockReason }
      );
      return thunkApi.fulfillWithValue(data);
    }
    catch (errors) {
      return thunkApi.rejectWithValue(errors.response.data);
    }
  }
)

export const unblockProfile = createAsyncThunk(
  "users/unblockProfile",
  async (args, thunkApi) => {
    try {
      const { data } = await axiosInstance.post(
        `/users/profile/unblock`,
        { userId: args.userId }
      );
      return thunkApi.fulfillWithValue(data);
    }
    catch (errors) {
      return thunkApi.rejectWithValue(errors.response.data);
    }
  }
)

const usersSlice = createSlice({
  name: "users",
  initialState,
  reducers: {
    resetUsers: (state) => {
      state.isLoading = false;
      state.data = [];
      state.userDetails = null;
      state.itemsCount = 0;
      state.pages = 0;
      state.error = null;
    },
    clearCurrentUser: (state) => {
      state.currentUser = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getUsers.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getUsers.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.data = payload.data?.data || [];
        state.itemsCount = payload.data?.itemsCount || 0;
        state.pages = payload.data?.pages || 0;
        state.error = null;
      })
      .addCase(getUsers.rejected, (state, { payload }) => {
        state.isLoading = false;
        state.error = payload?.message
      })

      .addCase(createUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createUser.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.data.push(payload.data);
        state.itemsCount++;
        state.error = null;
      })
      .addCase(createUser.rejected, (state, { payload }) => {
        state.isLoading = false;
        state.error = payload?.message;
      })

      .addCase(updateUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateUser.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        const indexAt = state.data.findIndex(el => el._id === payload.data._id);
        state.data[indexAt] = payload.data;
        state.error = null;
      })
      .addCase(updateUser.rejected, (state, { payload }) => {
        state.isLoading = false;
        state.error = payload?.message;
      })

      .addCase(updatePassword.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updatePassword.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.error = null;
        const indexAt = state.data.findIndex(el => el._id === payload.data._id);
        state.data[indexAt] = payload.data;
      })
      .addCase(updatePassword.rejected, (state, { payload }) => {
        state.isLoading = false;
        state.error = payload?.message;
      })

      .addCase(deleteUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteUser.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.data = state.data.filter(el => el._id !== payload.data._id);
        state.error = null;
      })
      .addCase(deleteUser.rejected, (state, { payload }) => {
        state.isLoading = false;
        state.error = payload?.message;
      })

      .addCase(getUserDetails.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getUserDetails.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.error = null;
        state.userDetails = payload.data;
      })
      .addCase(getUserDetails.rejected, (state, { payload }) => {
        state.isLoading = false;
        state.error = payload?.message;
      })

      .addCase(getOwnerDetails.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getOwnerDetails.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.error = null;
        state.userDetails = payload.data;
      })
      .addCase(getOwnerDetails.rejected, (state, { payload }) => {
        state.isLoading = false;
        state.error = payload;
      })

      .addCase(blockProfile.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(blockProfile.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(blockProfile.rejected, (state, { payload }) => {
        state.isLoading = false;
        state.error = payload?.message;
      })

      .addCase(unblockProfile.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(unblockProfile.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(unblockProfile.rejected, (state, { payload }) => {
        state.isLoading = false;
        state.error = payload?.message;
      })
      .addCase(getCurrentUser.fulfilled, (state, { payload }) => {
        state.currentUser = payload.data;
      })
  }
});

export const { resetUsers, clearCurrentUser  } = usersSlice.actions;

export default usersSlice.reducer;